import type {
  LessonPptxOptions,
  GradeSummaryPptxOptions,
  AnalyticsPptxOptions,
} from './types';
import { extractSlideContent } from './slide-content-extractor';
import { formatReportDate, calculateGradeStats, getLetterGrade } from './export-helpers';

const COLORS = {
  primary: '2E4057',
  accent: '4A90D9',
  white: 'FFFFFF',
  lightGray: 'F5F5F5',
  darkGray: '333333',
  green: '228B22',
  headerBg: '2E4057',
};

// ============================================================
// LESSON PRESENTATION
// ============================================================

export async function generateLessonPptx(options: LessonPptxOptions): Promise<void> {
  const PptxGenJS = (await import('pptxgenjs')).default;

  const { lesson, slides, includeAnswers = false } = options;
  const sortedSlides = [...slides].sort((a, b) => a.slide_order - b.slide_order);

  const pres = new PptxGenJS();
  pres.layout = 'LAYOUT_WIDE';
  pres.author = 'EduLive JHS';
  pres.title = lesson.title;

  // Title slide
  const titleSlide = pres.addSlide();
  titleSlide.background = { color: COLORS.primary };
  titleSlide.addText(lesson.title, {
    x: 0.5,
    y: 1.5,
    w: 12.33,
    h: 2,
    fontSize: 40,
    bold: true,
    color: COLORS.white,
    align: 'center',
    valign: 'middle',
  });
  if (lesson.description) {
    titleSlide.addText(lesson.description, {
      x: 1.5,
      y: 3.8,
      w: 10.33,
      h: 1,
      fontSize: 18,
      color: 'CCCCCC',
      align: 'center',
      valign: 'top',
    });
  }
  titleSlide.addText(
    `${lesson.mode === 'live' ? 'Live' : 'Student-Paced'} Lesson  |  ${slides.length} Slides`,
    {
      x: 0.5,
      y: 6.2,
      w: 12.33,
      h: 0.5,
      fontSize: 12,
      color: '999999',
      align: 'center',
    }
  );

  // Content slides
  sortedSlides.forEach((slide, i) => {
    const extracted = extractSlideContent(slide, i);
    const pptxSlide = pres.addSlide();

    // Slide number badge
    pptxSlide.addText(`${extracted.slideNumber}`, {
      x: 0.3,
      y: 0.2,
      w: 0.5,
      h: 0.5,
      fontSize: 12,
      color: COLORS.white,
      align: 'center',
      valign: 'middle',
      shape: 'ellipse' as any,
      fill: { color: COLORS.accent },
    });

    // Type label
    pptxSlide.addText(`[${extracted.typeLabel}]`, {
      x: 1.0,
      y: 0.25,
      w: 3,
      h: 0.4,
      fontSize: 10,
      italic: true,
      color: '888888',
    });

    // Title
    pptxSlide.addText(extracted.title, {
      x: 0.5,
      y: 0.8,
      w: 12.33,
      h: 0.8,
      fontSize: 28,
      bold: true,
      color: COLORS.darkGray,
    });

    if (extracted.category === 'content') {
      // Content slide layout
      if (extracted.mainText) {
        pptxSlide.addText(extracted.mainText, {
          x: 0.5,
          y: 1.8,
          w: 12.33,
          h: 3.5,
          fontSize: 16,
          color: '444444',
          valign: 'top',
          wrap: true,
        });
      }

      if (extracted.secondaryText) {
        pptxSlide.addText(extracted.secondaryText, {
          x: 0.5,
          y: 5.5,
          w: 12.33,
          h: 0.8,
          fontSize: 12,
          italic: true,
          color: '888888',
        });
      }

      if (extracted.mediaUrl) {
        pptxSlide.addText(`[Media: ${extracted.mediaUrl}]`, {
          x: 0.5,
          y: 6.3,
          w: 12.33,
          h: 0.4,
          fontSize: 10,
          italic: true,
          color: 'AAAAAA',
        });
      }
    } else {
      // Activity slide layout
      if (extracted.mainText) {
        pptxSlide.addText(extracted.mainText, {
          x: 0.5,
          y: 1.8,
          w: 12.33,
          h: 1.2,
          fontSize: 20,
          color: COLORS.darkGray,
          valign: 'top',
          wrap: true,
        });
      }

      // Options/items
      if (extracted.items && extracted.items.length > 0) {
        const itemsText = extracted.items.map((item) => ({ text: item, options: { bullet: true, fontSize: 14, color: '444444' } }));
        pptxSlide.addText(itemsText as any, {
          x: 1.0,
          y: 3.2,
          w: 11,
          h: 2.5,
          valign: 'top',
        });
      } else if (extracted.secondaryText) {
        pptxSlide.addText(extracted.secondaryText, {
          x: 0.7,
          y: 3.2,
          w: 12,
          h: 2.5,
          fontSize: 14,
          color: '444444',
          valign: 'top',
          wrap: true,
        });
      }

      // Points badge
      if (extracted.points !== undefined) {
        pptxSlide.addText(`${extracted.points} pts`, {
          x: 11.5,
          y: 0.2,
          w: 1.5,
          h: 0.4,
          fontSize: 11,
          bold: true,
          color: COLORS.white,
          align: 'center',
          valign: 'middle',
          fill: { color: COLORS.accent },
          rectRadius: 0.1,
        });
      }

      // Answer
      if (includeAnswers && extracted.answerText) {
        pptxSlide.addText(`Answer: ${extracted.answerText}`, {
          x: 0.5,
          y: 6.0,
          w: 12.33,
          h: 0.5,
          fontSize: 13,
          bold: true,
          color: COLORS.green,
          fill: { color: 'F0FFF0' },
        });
      }
    }

    // Footer
    pptxSlide.addText('EduLive JHS', {
      x: 0.5,
      y: 7.0,
      w: 12.33,
      h: 0.3,
      fontSize: 8,
      color: 'CCCCCC',
      align: 'right',
    });
  });

  await pres.writeFile({ fileName: `lesson-${lesson.title.replace(/[^a-zA-Z0-9]/g, '-')}.pptx` });
}

// ============================================================
// GRADE SUMMARY PRESENTATION
// ============================================================

export async function generateGradeSummaryPptx(options: GradeSummaryPptxOptions): Promise<void> {
  const PptxGenJS = (await import('pptxgenjs')).default;

  const { lessonTitle, students, generatedDate } = options;
  const stats = calculateGradeStats(students);

  const pres = new PptxGenJS();
  pres.layout = 'LAYOUT_WIDE';
  pres.author = 'EduLive JHS';
  pres.title = `Grade Summary - ${lessonTitle}`;

  // Title slide
  const titleSlide = pres.addSlide();
  titleSlide.background = { color: COLORS.primary };
  titleSlide.addText(`Grade Summary`, {
    x: 0.5, y: 1.5, w: 12.33, h: 1.5,
    fontSize: 36, bold: true, color: COLORS.white, align: 'center',
  });
  titleSlide.addText(lessonTitle, {
    x: 0.5, y: 3.2, w: 12.33, h: 1,
    fontSize: 22, color: 'BBBBBB', align: 'center',
  });
  titleSlide.addText(formatReportDate(generatedDate), {
    x: 0.5, y: 6.2, w: 12.33, h: 0.5,
    fontSize: 12, color: '888888', align: 'center',
  });

  // Stats slide
  const statsSlide = pres.addSlide();
  statsSlide.addText('Class Overview', {
    x: 0.5, y: 0.3, w: 12.33, h: 0.8,
    fontSize: 28, bold: true, color: COLORS.darkGray,
  });

  const statBoxes = [
    { label: 'Students', value: `${stats.totalStudents}`, color: '4A90D9' },
    { label: 'Class Average', value: `${stats.classAverage}%`, color: '27AE60' },
    { label: 'Highest', value: `${stats.highest}%`, color: '2ECC71' },
    { label: 'Lowest', value: `${stats.lowest}%`, color: 'E74C3C' },
    { label: 'Passing (≥70%)', value: `${stats.passingCount}`, color: '9B59B6' },
  ];

  statBoxes.forEach((box, i) => {
    const x = 0.3 + i * 2.6;
    statsSlide.addText(box.value, {
      x, y: 2.0, w: 2.3, h: 1.5,
      fontSize: 36, bold: true, color: box.color, align: 'center', valign: 'middle',
      fill: { color: COLORS.lightGray },
      rectRadius: 0.1,
    });
    statsSlide.addText(box.label, {
      x, y: 3.6, w: 2.3, h: 0.5,
      fontSize: 12, color: '666666', align: 'center',
    });
  });

  // Grade distribution slide
  const distSlide = pres.addSlide();
  distSlide.addText('Grade Distribution', {
    x: 0.5, y: 0.3, w: 12.33, h: 0.8,
    fontSize: 28, bold: true, color: COLORS.darkGray,
  });

  const distData: Record<string, number> = { A: 0, B: 0, C: 0, D: 0, F: 0 };
  students.forEach((s) => { distData[getLetterGrade(s.percentage)]++; });

  const distRows: any[][] = [
    [
      { text: 'Grade', options: { bold: true, color: COLORS.white, fill: { color: COLORS.headerBg }, align: 'center' } },
      { text: 'Count', options: { bold: true, color: COLORS.white, fill: { color: COLORS.headerBg }, align: 'center' } },
      { text: 'Percentage', options: { bold: true, color: COLORS.white, fill: { color: COLORS.headerBg }, align: 'center' } },
    ],
  ];

  Object.entries(distData).forEach(([grade, count]) => {
    const pct = stats.totalStudents > 0 ? Math.round((count / stats.totalStudents) * 100) : 0;
    distRows.push([
      { text: grade, options: { align: 'center', bold: true } },
      { text: `${count}`, options: { align: 'center' } },
      { text: `${pct}%`, options: { align: 'center' } },
    ]);
  });

  distSlide.addTable(distRows, {
    x: 3.0, y: 1.5, w: 7.33,
    fontSize: 14,
    border: { type: 'solid', pt: 0.5, color: 'DDDDDD' },
    colW: [2.44, 2.44, 2.44],
    rowH: [0.5, 0.4, 0.4, 0.4, 0.4, 0.4],
  });

  // Student data slides (paginate ~15 per slide)
  const rowsPerSlide = 15;
  for (let page = 0; page < Math.ceil(students.length / rowsPerSlide); page++) {
    const dataSlide = pres.addSlide();
    const pageStudents = students.slice(page * rowsPerSlide, (page + 1) * rowsPerSlide);
    const pageLabel = Math.ceil(students.length / rowsPerSlide) > 1
      ? ` (${page + 1}/${Math.ceil(students.length / rowsPerSlide)})`
      : '';

    dataSlide.addText(`Student Grades${pageLabel}`, {
      x: 0.5, y: 0.2, w: 12.33, h: 0.6,
      fontSize: 22, bold: true, color: COLORS.darkGray,
    });

    const tableRows: any[][] = [
      [
        { text: 'Student', options: { bold: true, color: COLORS.white, fill: { color: COLORS.headerBg }, align: 'center', fontSize: 10 } },
        { text: 'Score', options: { bold: true, color: COLORS.white, fill: { color: COLORS.headerBg }, align: 'center', fontSize: 10 } },
        { text: '%', options: { bold: true, color: COLORS.white, fill: { color: COLORS.headerBg }, align: 'center', fontSize: 10 } },
        { text: 'Grade', options: { bold: true, color: COLORS.white, fill: { color: COLORS.headerBg }, align: 'center', fontSize: 10 } },
        { text: 'Completion', options: { bold: true, color: COLORS.white, fill: { color: COLORS.headerBg }, align: 'center', fontSize: 10 } },
      ],
    ];

    pageStudents.forEach((s, i) => {
      const rowFill = i % 2 === 0 ? { fill: { color: 'F8F9FA' } } : {};
      tableRows.push([
        { text: s.student_name, options: { fontSize: 10, ...rowFill } },
        { text: `${s.total_score}/${s.max_possible_score}`, options: { align: 'center', fontSize: 10, ...rowFill } },
        { text: `${Math.round(s.percentage)}%`, options: { align: 'center', fontSize: 10, ...rowFill } },
        { text: getLetterGrade(s.percentage), options: { align: 'center', bold: true, fontSize: 10, ...rowFill } },
        { text: `${Math.round(s.completion_rate)}%`, options: { align: 'center', fontSize: 10, ...rowFill } },
      ]);
    });

    dataSlide.addTable(tableRows, {
      x: 0.5, y: 1.0, w: 12.33,
      fontSize: 10,
      border: { type: 'solid', pt: 0.5, color: 'DDDDDD' },
      rowH: [0.4, ...Array(pageStudents.length).fill(0.35)],
    });
  }

  await pres.writeFile({ fileName: `grade-summary-${lessonTitle.replace(/[^a-zA-Z0-9]/g, '-')}.pptx` });
}

// ============================================================
// ANALYTICS DASHBOARD PRESENTATION
// ============================================================

export async function generateAnalyticsPptx(options: AnalyticsPptxOptions): Promise<void> {
  const PptxGenJS = (await import('pptxgenjs')).default;

  const { reportTitle, lessonReports, studentReports, sessionAnalytics, generatedDate } = options;

  const pres = new PptxGenJS();
  pres.layout = 'LAYOUT_WIDE';
  pres.author = 'EduLive JHS';
  pres.title = reportTitle;

  // Title slide
  const titleSlide = pres.addSlide();
  titleSlide.background = { color: COLORS.primary };
  titleSlide.addText(reportTitle, {
    x: 0.5, y: 2.0, w: 12.33, h: 2,
    fontSize: 36, bold: true, color: COLORS.white, align: 'center', valign: 'middle',
  });
  titleSlide.addText(formatReportDate(generatedDate), {
    x: 0.5, y: 6.2, w: 12.33, h: 0.5,
    fontSize: 12, color: '999999', align: 'center',
  });

  // Lesson summary slides
  if (lessonReports && lessonReports.length > 0) {
    const rowsPerSlide = 12;
    for (let page = 0; page < Math.ceil(lessonReports.length / rowsPerSlide); page++) {
      const slide = pres.addSlide();
      const pageData = lessonReports.slice(page * rowsPerSlide, (page + 1) * rowsPerSlide);

      slide.addText('Lesson Summary', {
        x: 0.5, y: 0.2, w: 12.33, h: 0.6,
        fontSize: 24, bold: true, color: COLORS.darkGray,
      });

      const headerOpts = { bold: true, color: COLORS.white, fill: { color: COLORS.headerBg }, align: 'center' as const, fontSize: 10 };
      const rows: any[][] = [
        [
          { text: 'Lesson', options: headerOpts },
          { text: 'Mode', options: headerOpts },
          { text: 'Sessions', options: headerOpts },
          { text: 'Students', options: headerOpts },
          { text: 'Avg Score', options: headerOpts },
          { text: 'Completion', options: headerOpts },
        ],
      ];

      pageData.forEach((r, i) => {
        const rowFill = i % 2 === 0 ? { fill: { color: 'F8F9FA' } } : {};
        rows.push([
          { text: r.lesson_title, options: { fontSize: 10, ...rowFill } },
          { text: r.mode === 'live' ? 'Live' : 'S-P', options: { align: 'center', fontSize: 10, ...rowFill } },
          { text: `${r.total_sessions}`, options: { align: 'center', fontSize: 10, ...rowFill } },
          { text: `${r.total_students}`, options: { align: 'center', fontSize: 10, ...rowFill } },
          { text: `${Math.round(r.avg_score)}%`, options: { align: 'center', fontSize: 10, ...rowFill } },
          { text: `${Math.round(r.avg_completion_rate)}%`, options: { align: 'center', fontSize: 10, ...rowFill } },
        ]);
      });

      slide.addTable(rows, {
        x: 0.5, y: 1.0, w: 12.33,
        border: { type: 'solid', pt: 0.5, color: 'DDDDDD' },
        rowH: [0.4, ...Array(pageData.length).fill(0.35)],
      });
    }
  }

  // Student performance slides
  if (studentReports && studentReports.length > 0) {
    const rowsPerSlide = 12;
    for (let page = 0; page < Math.ceil(studentReports.length / rowsPerSlide); page++) {
      const slide = pres.addSlide();
      const pageData = studentReports.slice(page * rowsPerSlide, (page + 1) * rowsPerSlide);

      slide.addText('Student Performance', {
        x: 0.5, y: 0.2, w: 12.33, h: 0.6,
        fontSize: 24, bold: true, color: COLORS.darkGray,
      });

      const headerOpts = { bold: true, color: COLORS.white, fill: { color: COLORS.headerBg }, align: 'center' as const, fontSize: 10 };
      const rows: any[][] = [
        [
          { text: 'Student', options: headerOpts },
          { text: 'Sessions', options: headerOpts },
          { text: 'Avg Score', options: headerOpts },
          { text: 'Completion', options: headerOpts },
          { text: 'Passing', options: headerOpts },
        ],
      ];

      pageData.forEach((r, i) => {
        const rowFill = i % 2 === 0 ? { fill: { color: 'F8F9FA' } } : {};
        rows.push([
          { text: r.student_name, options: { fontSize: 10, ...rowFill } },
          { text: `${r.sessions_attended}`, options: { align: 'center', fontSize: 10, ...rowFill } },
          { text: `${Math.round(r.avg_percentage)}%`, options: { align: 'center', fontSize: 10, ...rowFill } },
          { text: `${Math.round(r.avg_completion_rate)}%`, options: { align: 'center', fontSize: 10, ...rowFill } },
          { text: `${r.passing_sessions}/${r.total_graded_sessions}`, options: { align: 'center', fontSize: 10, ...rowFill } },
        ]);
      });

      slide.addTable(rows, {
        x: 0.5, y: 1.0, w: 12.33,
        border: { type: 'solid', pt: 0.5, color: 'DDDDDD' },
        rowH: [0.4, ...Array(pageData.length).fill(0.35)],
      });
    }
  }

  // Session analytics slides
  if (sessionAnalytics && sessionAnalytics.length > 0) {
    const rowsPerSlide = 12;
    for (let page = 0; page < Math.ceil(sessionAnalytics.length / rowsPerSlide); page++) {
      const slide = pres.addSlide();
      const pageData = sessionAnalytics.slice(page * rowsPerSlide, (page + 1) * rowsPerSlide);

      slide.addText('Session Analytics', {
        x: 0.5, y: 0.2, w: 12.33, h: 0.6,
        fontSize: 24, bold: true, color: COLORS.darkGray,
      });

      const headerOpts = { bold: true, color: COLORS.white, fill: { color: COLORS.headerBg }, align: 'center' as const, fontSize: 10 };
      const rows: any[][] = [
        [
          { text: 'Date', options: headerOpts },
          { text: 'Participants', options: headerOpts },
          { text: 'Avg Score', options: headerOpts },
          { text: 'Completion', options: headerOpts },
          { text: 'Submissions', options: headerOpts },
          { text: 'Security', options: headerOpts },
          { text: 'Duration', options: headerOpts },
        ],
      ];

      pageData.forEach((r, i) => {
        const rowFill = i % 2 === 0 ? { fill: { color: 'F8F9FA' } } : {};
        rows.push([
          { text: r.started_at ? new Date(r.started_at).toLocaleDateString() : 'N/A', options: { align: 'center', fontSize: 10, ...rowFill } },
          { text: `${r.total_participants}`, options: { align: 'center', fontSize: 10, ...rowFill } },
          { text: `${Math.round(r.avg_score)}%`, options: { align: 'center', fontSize: 10, ...rowFill } },
          { text: `${Math.round(r.avg_completion_rate)}%`, options: { align: 'center', fontSize: 10, ...rowFill } },
          { text: `${r.total_submissions}`, options: { align: 'center', fontSize: 10, ...rowFill } },
          { text: `${r.tab_switch_incidents} / ${r.app_switch_incidents} / ${r.screenshot_incidents}`, options: { align: 'center', fontSize: 10, ...rowFill } },
          { text: `${r.session_duration_minutes} min`, options: { align: 'center', fontSize: 10, ...rowFill } },
        ]);
      });

      slide.addTable(rows, {
        x: 0.5, y: 1.0, w: 12.33,
        border: { type: 'solid', pt: 0.5, color: 'DDDDDD' },
        rowH: [0.4, ...Array(pageData.length).fill(0.35)],
      });
    }
  }

  await pres.writeFile({ fileName: `${reportTitle.replace(/[^a-zA-Z0-9]/g, '-')}.pptx` });
}
